/**
 * The `tool` conversation row a turn writes for each tool call (`engine/src/agentic/turn-tool-call.ts`), and how the
 * thread reads it back.
 *
 * Until 2026-10-17 the row held only the call's name, id, status and iteration: the input and the result lived in the
 * turn's memory and its events, so a conversation reopened later showed no tool calls at all (the client dropped the
 * rows it could not draw). The row now keeps what the call was given and what it returned, each cut to a size a thread
 * can show; the full output stays where it always was, in `tool_executions` and the turn's own context.
 */
import { z } from 'zod';

export const UNIFIED_TOOL_CALL_KIND = 'unified_tool_call';

/** The most of a call's input a row keeps. */
export const TOOL_ROW_INPUT_LIMIT = 2_000;
/** The most of a call's result a row keeps. */
export const TOOL_ROW_RESULT_LIMIT = 4_000;

export const UnifiedToolCallRowSchema = z.object({
  kind: z.literal(UNIFIED_TOOL_CALL_KIND),
  toolName: z.string(),
  toolUseId: z.string(),
  /** `ok` or `error`, as the dispatcher answered. */
  status: z.string(),
  iteration: z.number(),
  /** The call's input as JSON, cut to `TOOL_ROW_INPUT_LIMIT`. Absent on rows written before 2026-10-17. */
  input: z.string().optional(),
  /** What the model saw of the result (`agentView` when the tool gives one) as JSON, cut to `TOOL_ROW_RESULT_LIMIT`. */
  result: z.string().optional(),
  /** Why the call failed, when it did. */
  error: z.string().optional(),
});

export type UnifiedToolCallRow = z.infer<typeof UnifiedToolCallRowSchema>;

/** `value` as JSON, cut to `limit` characters with a marker saying so. */
export function previewJson(value: unknown, limit: number): string {
  let text: string;
  try {
    text = typeof value === 'string' ? value : (JSON.stringify(value, null, 2) ?? 'null');
  } catch {
    text = String(value);
  }
  if (text.length <= limit) return text;
  return `${text.slice(0, limit)}\n… (${text.length - limit} more characters not kept)`;
}

/** The row content for one finished call. */
export function buildUnifiedToolCallRow(call: {
  toolName: string;
  toolUseId: string;
  status: string;
  iteration: number;
  input: unknown;
  result?: unknown;
  error?: string;
}): string {
  const row: UnifiedToolCallRow = {
    kind: UNIFIED_TOOL_CALL_KIND,
    toolName: call.toolName,
    toolUseId: call.toolUseId,
    status: call.status,
    iteration: call.iteration,
    input: previewJson(call.input ?? {}, TOOL_ROW_INPUT_LIMIT),
    ...(call.result !== undefined ? { result: previewJson(call.result, TOOL_ROW_RESULT_LIMIT) } : {}),
    ...(call.error !== undefined ? { error: call.error } : {}),
  };
  return JSON.stringify(row);
}

/** A row's content read back as a tool call, or `null` when it is not one. */
export function parseUnifiedToolCallRow(content: string): UnifiedToolCallRow | null {
  if (!content.startsWith('{')) return null;
  try {
    const parsed = UnifiedToolCallRowSchema.safeParse(JSON.parse(content));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
